import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Caption1,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Option,
  Text,
  Textarea,
  tokens,
} from '@fluentui/react-components';
import { useApi } from '../api/useApi';
import { useAsync } from '../api/useAsync';
import { PageHeader } from '../components/PageHeader';
import { UserPicker } from '../features/users/UserPicker';
import '../features/groups/groups.css';
import type { ActionResponse, DirectoryUserDto } from '../api/types';

function PeopleField({
  label,
  hint,
  people,
  onAdd,
  onRemove,
}: {
  label: string;
  hint?: string;
  people: DirectoryUserDto[];
  onAdd: (user: DirectoryUserDto) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <Field label={label} hint={hint}>
      <UserPicker onSelect={(u) => u && onAdd(u)} placeholder="Person suchen und hinzufügen…" clearOnSelect />
      {people.length ? (
        <div className="grp-chips">
          {people.map((u) => (
            <span className="grp-chip" key={u.id}>
              <span className="pii">{u.displayName}</span>
              {u.userType === 'Guest' ? <span style={{ color: '#b3261e', fontWeight: 600 }}>&nbsp;(Gast)</span> : null}
              <button type="button" aria-label={`${u.displayName} entfernen`} onClick={() => onRemove(u.id)}>
                ✕
              </button>
            </span>
          ))}
        </div>
      ) : null}
    </Field>
  );
}

export function TeamsCreatePage() {
  const api = useApi();
  const teamTypes = useAsync(() => api.getTeamTypes(), [api]);
  const policies = useAsync(() => api.getPagePolicies(), [api]);
  const ticketMode = policies.data?.find((p) => p.pageKey === 'teams.create')?.ticketMode ?? 'None';
  const types = useMemo(() => (teamTypes.data ?? []).filter((t) => t.enabled), [teamTypes.data]);

  const [typeId, setTypeId] = useState<string | undefined>();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [owners, setOwners] = useState<DirectoryUserDto[]>([]);
  const [members, setMembers] = useState<DirectoryUserDto[]>([]);
  const [ticket, setTicket] = useState('');
  const [nameError, setNameError] = useState<string | undefined>();
  const [ticketError, setTicketError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<ActionResponse | null>(null);
  const [guestWarn, setGuestWarn] = useState<string | undefined>();

  useEffect(() => {
    if (!types.some((t) => t.id === typeId)) setTypeId(types[0]?.id);
  }, [types, typeId]);

  const type = types.find((t) => t.id === typeId);
  const finalName = `${type?.prefix ?? ''}${name.trim()}`;
  const ownersOk = owners.length >= 2;
  const allowGuests = type?.allowGuests ?? true;
  const isGuest = (u: DirectoryUserDto) => u.userType === 'Guest';
  const guestsPresent = !allowGuests && [...owners, ...members].some(isGuest);

  const addUnique = (list: DirectoryUserDto[], u: DirectoryUserDto) =>
    list.some((x) => x.id === u.id) ? list : [...list, u];

  // Gäste-Policy der Team-Art durchsetzen: erlaubt sie keine Gäste, wird das
  // Hinzufügen eines Gastkontos verhindert und ein Hinweis gezeigt.
  function addPerson(setList: typeof setOwners, u: DirectoryUserDto) {
    if (!allowGuests && isGuest(u)) {
      setGuestWarn(`„${u.displayName}" ist ein Gastkonto — die Team-Art „${type?.name}" erlaubt keine Gäste.`);
      return;
    }
    setGuestWarn(undefined);
    setList((p) => addUnique(p, u));
  }

  function reset() {
    setName('');
    setDescription('');
    setOwners([]);
    setMembers([]);
    setTicket('');
    setDone(null);
    setError(null);
    setNameError(undefined);
    setTicketError(undefined);
  }

  async function submit() {
    if (!name.trim()) {
      setNameError('Ein Team-Name ist erforderlich.');
      return;
    }
    if (!ownersOk) return;
    if (guestsPresent) {
      setError('Diese Team-Art erlaubt keine Gäste — bitte die markierten Gastkonten entfernen.');
      return;
    }
    if (ticketMode === 'Required' && !ticket.trim()) {
      setTicketError('Ticketnummer ist erforderlich.');
      return;
    }
    setNameError(undefined);
    setTicketError(undefined);
    setBusy(true);
    setError(null);
    try {
      const res = await api.createTeam({
        displayName: name.trim(),
        description: description.trim() || undefined,
        teamTypeId: types.length ? typeId : undefined,
        ownerIds: owners.map((o) => o.id),
        memberIds: members.map((m) => m.id),
        ticketNumber: ticket.trim() || undefined,
      });
      setDone(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div>
        <PageHeader title="Team erstellen" />
        <div style={{ maxWidth: '640px', display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL }}>
          {done.status === 'PendingApproval' ? (
            <MessageBar intent="info">
              <MessageBarBody>
                <MessageBarTitle>Antrag eingereicht</MessageBarTitle>
                Die Anlage ist freigabepflichtig. Den Status findest du unter „Meine Anfragen".
              </MessageBarBody>
            </MessageBar>
          ) : (
            <MessageBar intent="success">
              <MessageBarBody>
                <MessageBarTitle>Erledigt</MessageBarTitle>
                {done.message}
              </MessageBarBody>
            </MessageBar>
          )}
          <div>
            <Button appearance="primary" onClick={reset}>
              Weiteres Team
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Team erstellen"
        description="Microsoft-Teams-Team mit Namenskonvention und mindestens zwei Ownern."
      />
      <div style={{ maxWidth: '560px', display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL }}>
        {error ? (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        ) : null}

        {types.length ? (
          <Field label="Team-Art" hint="Bestimmt Namens-Präfix und Gast-Policy (Governance).">
            <Dropdown
              value={type ? `${type.name}${type.description ? ` — ${type.description}` : ''}` : ''}
              selectedOptions={typeId ? [typeId] : []}
              onOptionSelect={(_, d) => d.optionValue && setTypeId(d.optionValue)}
            >
              {types.map((t) => (
                <Option key={t.id} value={t.id} text={`${t.name}${t.description ? ` — ${t.description}` : ''}`}>
                  {t.name}
                  {t.description ? <i style={{ color: tokens.colorNeutralForeground3 }}> — {t.description}</i> : null}
                </Option>
              ))}
            </Dropdown>
          </Field>
        ) : null}

        <Field label="Name" required validationMessage={nameError}>
          <Input value={name} onChange={(_, d) => setName(d.value)} placeholder="z. B. Projekt Phoenix" />
        </Field>

        <Field label="Beschreibung">
          <Textarea value={description} onChange={(_, d) => setDescription(d.value)} rows={2} placeholder="Optionaler Zweck des Teams" />
        </Field>

        <PeopleField
          label="Owner (mindestens 2)"
          hint={ownersOk ? `${owners.length} Owner gewählt.` : `Noch ${2 - owners.length} Owner nötig (mindestens 2).`}
          people={owners}
          onAdd={(u) => addPerson(setOwners, u)}
          onRemove={(id) => setOwners((p) => p.filter((x) => x.id !== id))}
        />
        <PeopleField
          label="Mitglieder"
          hint={allowGuests ? 'Optional.' : 'Optional — diese Team-Art erlaubt keine Gäste.'}
          people={members}
          onAdd={(u) => addPerson(setMembers, u)}
          onRemove={(id) => setMembers((p) => p.filter((x) => x.id !== id))}
        />

        {guestWarn || guestsPresent ? (
          <MessageBar intent="warning">
            <MessageBarBody>
              <MessageBarTitle>Gäste nicht erlaubt</MessageBarTitle>
              {guestsPresent
                ? 'Diese Team-Art erlaubt keine Gäste. Bitte die mit „(Gast)" markierten Gastkonten entfernen, bevor du das Team anlegst.'
                : guestWarn}
            </MessageBarBody>
          </MessageBar>
        ) : null}

        {name.trim() ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: tokens.spacingHorizontalS,
              padding: tokens.spacingVerticalS + ' ' + tokens.spacingHorizontalM,
              borderRadius: tokens.borderRadiusLarge,
              backgroundColor: tokens.colorNeutralBackground2,
            }}
          >
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Vollständiger Name:</Caption1>
            <Text weight="semibold">{finalName}</Text>
            {type ? (
              <Badge appearance="tint" color={type.allowGuests ? 'warning' : 'success'}>
                {type.allowGuests ? 'Gäste erlaubt' : 'ohne Gäste'}
              </Badge>
            ) : null}
          </div>
        ) : null}

        {ticketMode !== 'None' ? (
          <Field
            label="Ticketnummer"
            required={ticketMode === 'Required'}
            validationMessage={ticketError}
            hint={ticketMode === 'Required' ? 'Pflicht für diese Aktion.' : 'Optional.'}
          >
            <Input value={ticket} onChange={(_, d) => setTicket(d.value)} placeholder="z. B. REQ0012345" />
          </Field>
        ) : null}

        <div>
          <Button appearance="primary" disabled={!name.trim() || !ownersOk || guestsPresent || busy} onClick={submit}>
            {busy ? 'Bitte warten…' : 'Team anlegen'}
          </Button>
        </div>
      </div>
    </div>
  );
}
